import { useEffect, useState, useCallback, useRef, Fragment } from "react";
import { toast } from "sonner";
import { Loader2, Ban, X, Trash2, Info, Link2, Home, DollarSign, ChevronDown, ChevronRight } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import BookingDetailDialog from "@/components/workspace/BookingDetailDialog";
import Portal from "@/components/workspace/Portal";
import { computeAutoBlockedDays } from "@/lib/availability";

const CH = {
  Atiti: { bg: "#4F46E5", label: "a", full: "Atiti" },
  Airbnb: { bg: "#FF385C", label: "A", full: "Airbnb" },
  "Booking.com": { bg: "#003580", label: "B.", full: "Booking.com" },
  Direct: { bg: "#10B981", label: "D", full: "Direct/Offline" },
  MakeMyTrip: { bg: "#EB2226", label: "M", full: "MakeMyTrip" },
};
const ChannelBadge = ({ channel }) => {
  const c = CH[channel] || { bg: "#64748B", label: "?", full: channel };
  return (
    <span title={c.full} className="w-5 h-5 rounded-[4px] text-white text-[10px] font-extrabold flex items-center justify-center shrink-0 shadow-sm" style={{ backgroundColor: c.bg }}>
      {c.label}
    </span>
  );
};

const fmtRate = (n) => Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const today10 = () => new Date().toISOString().slice(0, 10);
const plusDays = (d, n) => { const x = new Date(d + "T00:00:00"); x.setDate(x.getDate() + n); return x.toISOString().slice(0, 10); };

const COL_W = 92; // px per day column
const UNIT_COL_W = 180;

export default function CalendarTab({ pid, month, refreshKey, onMonthChange }) {
  const [d, setD] = useState(null);
  const [tick, setTick] = useState(0);
  const [drawer, setDrawer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(null);
  const [editRate, setEditRate] = useState(null); // { date, value }
  const [collapsed, setCollapsed] = useState({}); // { groupKey: true }
  const [openBooking, setOpenBooking] = useState(null); // booking_id to open in the detail dialog
  const dragRef = useRef(null);
  // Batch-3 follow-up: when Stay View opens (or the month/operating-day
  // changes), auto-scroll the horizontal calendar so the Operating Day sits
  // near the left edge (with ~2 past days visible before it), matching the
  // user's mockup. `gridRef` points at the scroll container we wrap the
  // <table> in below.
  const gridRef = useRef(null);
  useEffect(() => {
    if (!d || !gridRef.current) return;
    if (!d.operating_day) return;
    const opMonth = d.operating_day.slice(0, 7);
    if (opMonth !== d.month) {
      // Operating day not in the displayed month — no auto-scroll (user is
      // browsing history/future via the slider).
      gridRef.current.scrollLeft = 0;
      return;
    }
    const opDay = parseInt(d.operating_day.slice(8, 10), 10);
    // Show 2 past days on the left; day-1 offsets to column 0.
    const target = Math.max(0, (opDay - 1 - 2) * COL_W);
    gridRef.current.scrollLeft = target;
  }, [d?.operating_day, d?.month, d?.days]);

  const load = useCallback(() => {
    setD(null);
    api.get(`/pms/properties/${pid}/calendar`, { params: { month } })
      .then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e)));
  }, [pid, month]);
  useEffect(() => { load(); }, [load, refreshKey, tick]);

  useEffect(() => {
    const up = () => {
      const ds = dragRef.current;
      if (!ds) return;
      dragRef.current = null; setDrag(null);
      const lo = Math.min(ds.anchor, ds.current);
      const hi = Math.max(ds.anchor, ds.current);
      setDrawer({ unit_id: ds.unit_id, unit_name: ds.unit_name,
                  start: `${month}-${String(lo).padStart(2, "0")}`,
                  end: plusDays(`${month}-${String(hi).padStart(2, "0")}`, 1),
                  reason: "Owner block" });
    };
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
  }, [month]);

  if (!d) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-300" size={30} /></div>;

  const days = Array.from({ length: d.days }, (_, i) => i + 1);
  // Direct-only auto-block maps — booking a unit stripes ONLY its direct
  // neighbours in `pms_links`, never a chained unit. Shared helper so
  // NewReservation + backend use the same rule set.

  const dayKey = (day) => `${month}-${String(day).padStart(2, "0")}`;
  const bookingByUnitStart = {}; // key = `${unit}-${startDay}` -> {booking, span}
  const bookingSpanCover = {}; // key `${unit}-${day}` -> true (day covered by a booking that started earlier)
  const blockByUnitStart = {};
  const blockSpanCover = {};

  const clampDay = (dateStr) => {
    const dt = new Date(dateStr + "T00:00:00");
    const [y, m] = month.split("-").map(Number);
    if (dt < new Date(y, m - 1, 1)) return 1;
    const last = new Date(y, m, 0).getDate();
    if (dt > new Date(y, m - 1, last)) return last + 1;
    return dt.getDate();
  };

  const bookingsClamped = [];
  for (const b of d.bookings) {
    if (!b.unit_id) continue;
    const startDay = clampDay(b.checkin);
    const endDay = clampDay(b.checkout); // exclusive
    const span = Math.max(1, endDay - startDay);
    bookingByUnitStart[`${b.unit_id}-${startDay}`] = { b, span };
    for (let dd = startDay; dd < endDay; dd++) {
      bookingSpanCover[`${b.unit_id}-${dd}`] = true;
    }
    bookingsClamped.push({ b, startDay, endDay });
  }

  // Same-day turnover detection: if a booking checks out on day X AND
  // another booking checks in on day X for the SAME unit, render day X as
  // a midday split cell (departing on left triangle, arriving on right).
  // The arriving booking is shifted to start rendering from X+1.
  const checkoutByUnitDay = {};
  for (const b of d.bookings) {
    if (!b.unit_id) continue;
    const co = clampDay(b.checkout);
    if (co >= 1 && co <= days.length) checkoutByUnitDay[`${b.unit_id}-${co}`] = b;
  }
  const turnoverByUnitDay = {};
  for (const key of Object.keys(bookingByUnitStart)) {
    const outgoing = checkoutByUnitDay[key];
    const { b: incoming, span } = bookingByUnitStart[key];
    if (outgoing && outgoing.id !== incoming.id) {
      turnoverByUnitDay[key] = { outgoing, incoming };
      // Shift incoming to render its remaining nights from X+1
      const [uid, dayStr] = key.split("-");
      const nextDay = parseInt(dayStr, 10) + 1;
      if (span > 1 && nextDay <= days.length) {
        bookingByUnitStart[`${uid}-${nextDay}`] = { b: incoming, span: span - 1 };
      }
      delete bookingByUnitStart[key];
    }
  }
  const blocksClamped = [];
  for (const bl of (d.blocks || [])) {
    const startDay = clampDay(bl.start);
    const endDay = clampDay(bl.end);
    const span = Math.max(1, endDay - startDay);
    blockByUnitStart[`${bl.unit_id}-${startDay}`] = { bl, span };
    for (let dd = startDay; dd < endDay; dd++) {
      blockSpanCover[`${bl.unit_id}-${dd}`] = true;
    }
    blocksClamped.push({ bl, startDay, endDay });
  }
  // Direct-only propagation via shared helper (host spec §3, Feb 2026).
  const { autoBookedDays, autoBlockedDays } = computeAutoBlockedDays({
    bookingsClamped, blocksClamped, unitLinks: d.links || [],
  });

  // Batch-2 Item 4: Stay View must anchor on this property's Night Audit
  // Operating Day, not the real system clock. Falls back to the JS date only
  // if the calendar hasn't returned operating_day yet (first render before
  // fetch completes).
  const opDayIso = d.operating_day || new Date().toISOString().slice(0, 10);
  const opDayMonth = opDayIso.slice(0, 7);
  const opDayDay = parseInt(opDayIso.slice(8, 10), 10);
  const isThisMonth = month === opDayMonth;
  const todayIso = opDayIso;
  const focusDay = isThisMonth ? opDayDay : 1;
  const focusIso = isThisMonth ? todayIso : `${month}-${String(focusDay).padStart(2, "0")}`;

  // Chip counts for focus day
  let occupied = 0, reserved = 0, blocked = 0, dueOut = 0, dirty = 0, vacant = 0;
  d.units.forEach((u) => {
    const key = `${u.id}-${focusDay}`;
    const covered = bookingSpanCover[key];
    const manual = blockSpanCover[key];
    const autoBooked = autoBookedDays[key];
    const autoBlocked = autoBlockedDays[key];
    if (covered) {
      // find the booking
      const owning = Object.values(bookingByUnitStart).find(({ b }) => b.unit_id === u.id && clampDay(b.checkin) <= focusDay && clampDay(b.checkout) > focusDay);
      if (owning) {
        if (["upcoming"].includes(owning.b.status)) reserved++;
        else occupied++;
        if (owning.b.checkout === focusIso) dueOut++;
      }
    } else if (manual || autoBlocked) blocked++;
    else if (autoBooked) occupied++;
    else vacant++;
    if (d.bookings.some((x) => x.unit_id === u.id && x.checkout === focusIso && !["cancelled", "no_show"].includes(x.status))) dirty++;
  });
  const chips = [
    { id: "all", label: "All", count: d.units.length, color: "bg-slate-900 text-white" },
    { id: "vacant", label: "Vacant", count: vacant, color: "bg-slate-100 text-slate-600" },
    { id: "occupied", label: "Occupied", count: occupied, color: "bg-emerald-50 text-emerald-600" },
    { id: "reserved", label: "Reserved", count: reserved, color: "bg-amber-50 text-amber-600" },
    { id: "blocked", label: "Blocked", count: blocked, color: "bg-slate-200 text-slate-700" },
    { id: "dueout", label: "Due Out", count: dueOut, color: "bg-sky-50 text-sky-600" },
    { id: "dirty", label: "Dirty", count: dirty, color: "bg-orange-50 text-orange-600" },
  ];

  const dayOccupancy = days.map((day) => {
    let occ = 0;
    d.units.forEach((u) => {
      const k = `${u.id}-${day}`;
      if (bookingSpanCover[k] || blockSpanCover[k] || autoBookedDays[k] || autoBlockedDays[k]) occ++;
    });
    return { day, occ, pct: d.units.length ? Math.round((occ / d.units.length) * 100) : 0, avail: d.units.length - occ };
  });

  const startDrag = (unit, day, e) => {
    e.preventDefault();
    const ds = { unit_id: unit.id, unit_name: unit.name, anchor: day, current: day };
    dragRef.current = ds; setDrag(ds);
  };
  const extendDrag = (unit, day) => {
    if (!dragRef.current || dragRef.current.unit_id !== unit.id) return;
    if (dragRef.current.current === day) return;
    const lo = Math.min(dragRef.current.anchor, day);
    const hi = Math.max(dragRef.current.anchor, day);
    for (let dd = lo; dd <= hi; dd++) {
      const k = `${unit.id}-${dd}`;
      if (bookingSpanCover[k] || blockSpanCover[k]) return;
    }
    const ds = { ...dragRef.current, current: day };
    dragRef.current = ds; setDrag(ds);
  };

  const saveBlock = async () => {
    setBusy(true);
    try {
      await api.post(`/pms/properties/${pid}/blocks`, { unit_id: drawer.unit_id, start: drawer.start, end: drawer.end, reason: drawer.reason });
      toast.success(`Unit ${drawer.unit_name} blocked ${drawer.start} → ${drawer.end}`);
      setDrawer(null); setTick(t => t + 1);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const deleteBlock = async () => {
    setBusy(true);
    try {
      await api.delete(`/pms/blocks/${drawer.existing.id}`);
      toast.success("Block removed. Inventory bookable again.");
      setDrawer(null); setTick(t => t + 1);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const openExistingBlock = (bl, unit) => setDrawer({ unit_id: unit.id, unit_name: unit.name, start: bl.start, end: bl.end, reason: bl.reason, existing: bl });

  const commitRate = async () => {
    if (!editRate) return;
    const rate = parseFloat(editRate.value);
    const original = d.rates.find((r) => r.date === editRate.date);
    setEditRate(null);
    if (isNaN(rate) || rate < 0) return;
    if (original && Math.round(rate) === original.rate) return; // no-op
    setBusy(true);
    try {
      await api.put(`/pms/properties/${pid}/rates`, { overrides: [{ date: editRate.date, rate, inventory: d.units.length }] });
      toast.success(`Rate for ${editRate.date} set to ₹${rate.toLocaleString("en-IN")} · pushed to every channel`);
      setTick((t) => t + 1);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const resetRate = async (dateStr) => {
    setBusy(true);
    try {
      await api.delete(`/pms/properties/${pid}/rates/${dateStr}`);
      toast.success(`Rate reset to city default on ${dateStr}`);
      setTick((t) => t + 1);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="reveal">
      {/* Top bar: filter chips + base rate + info */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <input
          data-testid="stay-view-date-search"
          type="date"
          value={focusIso}
          onChange={(e) => {
            const v = e.target.value;
            if (!v || !onMonthChange) return;
            const ym = v.slice(0, 7);
            if (ym !== month) onMonthChange(ym);
          }}
          title="Jump the calendar to any date"
          className="text-[11px] font-mono text-slate-700 bg-white border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40"
        />
        {focusIso !== opDayIso && onMonthChange && (
          <button
            data-testid="stay-view-today"
            onClick={() => onMonthChange(opDayMonth)}
            className="text-[11px] font-semibold text-[#4F46E5] bg-[#EEF2FF] hover:bg-[#E0E7FF] border border-[#C7D2FE] rounded-full px-3 py-1.5"
            title={`Jump to the property's current Operating Day (${opDayIso})`}
          >Operating Day</button>
        )}
        {/* Batch-2 Item 4: bidirectional slider — walk the calendar left/right */}
        {onMonthChange && (
          <div data-testid="stay-view-slider" className="flex items-center gap-1 bg-white border border-slate-200 rounded-full pl-1 pr-1 py-0.5">
            <button data-testid="stay-view-back-week" onClick={() => {
              const dt = new Date(focusIso + "T00:00:00"); dt.setDate(dt.getDate() - 7);
              onMonthChange(dt.toISOString().slice(0, 7));
            }} title="7 days earlier"
              className="h-7 w-7 rounded-full text-slate-500 hover:bg-slate-100 flex items-center justify-center">
              «
            </button>
            <button data-testid="stay-view-back-day" onClick={() => {
              const dt = new Date(focusIso + "T00:00:00"); dt.setDate(dt.getDate() - 1);
              onMonthChange(dt.toISOString().slice(0, 7));
            }} title="1 day earlier"
              className="h-7 w-7 rounded-full text-slate-500 hover:bg-slate-100 flex items-center justify-center">
              ‹
            </button>
            <span className="px-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider" title="Left = past bookings · Right = future bookings">
              Past ↔ Future
            </span>
            <button data-testid="stay-view-fwd-day" onClick={() => {
              const dt = new Date(focusIso + "T00:00:00"); dt.setDate(dt.getDate() + 1);
              onMonthChange(dt.toISOString().slice(0, 7));
            }} title="1 day later"
              className="h-7 w-7 rounded-full text-slate-500 hover:bg-slate-100 flex items-center justify-center">
              ›
            </button>
            <button data-testid="stay-view-fwd-week" onClick={() => {
              const dt = new Date(focusIso + "T00:00:00"); dt.setDate(dt.getDate() + 7);
              onMonthChange(dt.toISOString().slice(0, 7));
            }} title="7 days later"
              className="h-7 w-7 rounded-full text-slate-500 hover:bg-slate-100 flex items-center justify-center">
              »
            </button>
          </div>
        )}
        {chips.map((c) => (
          <div key={c.id} data-testid={`chip-${c.id}`} className={`px-3.5 py-1.5 rounded-full text-xs font-semibold flex items-center gap-2 ${c.color}`}>
            {c.label} <span className="bg-white/40 px-1.5 rounded-full text-[10px]">{c.count}</span>
          </div>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <div className="text-[11px] text-slate-500 bg-white border border-slate-200 rounded-lg px-3 py-2 font-semibold">Base rate ₹{d.base_rate}</div>
          <span title="Weekday rates use the property's ADR baseline. Fri/Sat are boosted by 15%. Rates roll into the P&L when a booking is created.">
            <Info size={16} className="text-slate-400" />
          </span>
        </div>
      </div>

      {/* Channel legend */}
      <div className="flex items-center gap-4 mb-3 text-[11px] font-semibold text-slate-500 flex-wrap">
        {Object.entries(CH).map(([ch, cfg]) => (
          <span key={ch} className="flex items-center gap-1.5"><ChannelBadge channel={ch} /> {cfg.full}</span>
        ))}
        <span className="flex items-center gap-1.5"><span className="w-4 h-4 rounded-sm" style={{ background: "repeating-linear-gradient(45deg,#e2e8f0,#e2e8f0 3px,#cbd5e1 3px,#cbd5e1 5px)" }} /> Auto-blocked (same-space)</span>
        <span className="flex items-center gap-1.5"><span className="w-4 h-4 rounded-sm" style={{ background: "repeating-linear-gradient(45deg,#1E293B,#1E293B 3px,#334155 3px,#334155 5px)" }} /> Blocked (all channels)</span>
        <span className="ml-auto italic text-slate-400 font-normal">Double-click a rate to override for that night · drag empty cells to block</span>
      </div>

      <div ref={gridRef} data-testid="calendar-grid"
        onWheel={(e) => {
          // If the user is scrolling vertically over the calendar with
          // no shift key, convert it to horizontal scroll so hovering
          // the calendar and spinning the wheel slides the dates. If
          // they hold shift OR are already scrolling horizontally,
          // leave the native behaviour alone.
          if (e.deltaX === 0 && e.deltaY !== 0 && !e.shiftKey) {
            e.currentTarget.scrollLeft += e.deltaY;
            e.preventDefault();
          }
        }}
        className="bg-white rounded-2xl border border-slate-200 overflow-x-auto overscroll-contain scroll-hint-right">
        <table className="border-collapse" style={{ minWidth: UNIT_COL_W + d.days * COL_W }}>
          <colgroup>
            <col style={{ width: UNIT_COL_W }} />
            {days.map((day) => <col key={day} style={{ width: COL_W }} />)}
          </colgroup>
          <thead>
            <tr className="bg-slate-50/60">
              <th className="sticky left-0 bg-slate-50/95 text-left text-[11px] uppercase tracking-wider text-slate-500 font-semibold px-4 py-3 border-b border-r border-slate-200 z-10 shadow-[2px_0_4px_-2px_rgba(15,23,42,0.08)]">Unit Type</th>
              {d.rates.map((r) => {
                const isToday = isThisMonth && r.day === opDayDay;
                return (
                  <th key={r.day} className={`text-center py-2 border-b border-r border-slate-100 ${r.weekend ? "bg-amber-50/30" : ""}`}>
                    <div className={`text-[10px] font-semibold uppercase tracking-wider ${isToday ? "brand-pink" : "text-slate-400"}`}>{r.weekday}</div>
                    <div className={`text-[13px] font-display font-bold ${isToday ? "brand-pink" : "text-slate-800"}`}>{r.label}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {(() => {
              // Derive unit-type group from unit name — numeric like "101" → "1st Floor · 100s"; alpha prefix like "3BHK-A" → "3BHK"
              const typeKey = (name) => {
                const num = name.match(/^(\d+)/);
                if (num) {
                  const n = parseInt(num[1], 10);
                  const hundred = Math.floor(n / 100);
                  if (hundred >= 1 && hundred <= 9) {
                    const suffix = ["Ground", "1st Floor", "2nd Floor", "3rd Floor", "4th Floor", "5th Floor", "6th Floor", "7th Floor", "8th Floor", "9th Floor"][hundred] || `Floor ${hundred}`;
                    return `${suffix} · ${hundred}00 series`;
                  }
                  return `Series ${hundred || n}`;
                }
                const a = name.match(/^([A-Za-z\d]+?)[- _]/);
                return a ? a[1].toUpperCase() : name;
              };
              const groups = [];
              const seen = {};
              d.units.forEach((u) => {
                const k = typeKey(u.name);
                if (!seen[k]) { seen[k] = { key: k, units: [] }; groups.push(seen[k]); }
                seen[k].units.push(u);
              });
              const showHeaders = groups.length > 1;
              let flatIdx = -1;
              return groups.map((g) => {
                const isCollapsed = collapsed[g.key];
                return (
                  <Fragment key={g.key}>
                    {showHeaders && (
                      <tr className="bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer" onClick={() => setCollapsed({ ...collapsed, [g.key]: !isCollapsed })}>
                        <td data-testid={`group-header-${g.key}`} className="sticky left-0 bg-slate-50 hover:bg-slate-100 px-4 py-2 border-y border-slate-200 z-10">
                          <div className="flex items-center gap-2">
                            {isCollapsed ? <ChevronRight size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}
                            <span className="font-display font-bold text-slate-800 text-xs uppercase tracking-wider">{g.key}</span>
                            <span className="text-[10px] font-mono bg-white border border-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">{g.units.length}</span>
                          </div>
                        </td>
                        <td colSpan={d.days} className="bg-slate-50 border-y border-slate-200 py-2 pr-4">
                          <div className="flex items-center justify-end gap-3 text-[10px] text-slate-400 font-semibold uppercase tracking-wider">
                            {isCollapsed ? "Click to expand" : `Click to collapse · ${g.units.length} unit${g.units.length > 1 ? "s" : ""}`}
                          </div>
                        </td>
                      </tr>
                    )}
                    {!isCollapsed && g.units.map((u) => { flatIdx++; return (
                      <UnitRows key={u.id}
                        unit={u}
                        isFirstUnit={flatIdx === 0}
                        days={days}
                        rates={d.rates}
                        editRate={editRate}
                        setEditRate={setEditRate}
                        commitRate={commitRate}
                        resetRate={resetRate}
                        bookingByUnitStart={bookingByUnitStart}
                        bookingSpanCover={bookingSpanCover}
                        turnoverByUnitDay={turnoverByUnitDay}
                        blockByUnitStart={blockByUnitStart}
                        blockSpanCover={blockSpanCover}
                        autoBookedDays={autoBookedDays}
                        autoBlockedDays={autoBlockedDays}
                        drag={drag}
                        startDrag={startDrag}
                        extendDrag={extendDrag}
                        openExistingBlock={openExistingBlock}
                        openBooking={(bid) => setOpenBooking(bid)}
                        isThisMonth={isThisMonth}
                        todayDay={opDayDay}
                      />
                    );})}
                  </Fragment>
                );
              });
            })()}
          </tbody>
          <tfoot className="bg-slate-50/60">
            <tr>
              <td className="sticky left-0 bg-slate-50/80 px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold border-t border-r border-slate-200 z-10">Available Inventory</td>
              {dayOccupancy.map((x) => (
                <td key={x.day} className="text-center py-2 border-t border-r border-slate-100 text-[11px] font-mono font-bold text-slate-600">{x.avail}</td>
              ))}
            </tr>
            <tr>
              <td className="sticky left-0 bg-slate-50/80 px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold border-r border-slate-200 z-10">Occupancy</td>
              {dayOccupancy.map((x) => (
                <td key={x.day} data-testid={`cal-occ-${x.day}`} className="text-center py-2 border-r border-slate-100 text-[11px] font-mono font-bold" style={{ color: x.pct >= 66 ? "#10B981" : x.pct >= 33 ? "#F59E0B" : "#94A3B8" }}>
                  <span className="inline-flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: x.pct >= 66 ? "#10B981" : x.pct >= 33 ? "#F59E0B" : "#CBD5E1" }} />
                    {x.pct}%
                  </span>
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="text-[11px] text-slate-400 mt-3">Bookings show the source channel logo at the start of the bar. Bookings, auto-blocks (same physical space) and manual blocks all count as occupied — direct, offline and OTA reservations for those dates are refused automatically.</p>

      {drawer && (
      <Portal>
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[80] grid place-items-center p-3 sm:p-4 md:p-6 overflow-y-auto pt-safe pb-safe pl-safe pr-safe" onClick={() => setDrawer(null)}>
          <div data-testid="block-drawer" className="bg-white w-[96vw] min-h-[92vh] max-h-[92vh] max-w-none rounded-2xl shadow-2xl p-5 md:p-8 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg text-slate-900 flex items-center gap-2"><Ban size={16} className="brand-pink" /> {drawer.existing ? "Manage block" : "Block dates"}</h3>
              <button data-testid="block-drawer-close" onClick={() => setDrawer(null)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <p className="text-xs text-slate-500 mt-2">Unit <span className="font-bold text-slate-800">{drawer.unit_name}</span> · blocked dates cannot be sold by <span className="font-semibold">any</span> channel (Atiti, direct/offline, or connected OTAs).</p>
            <div className="space-y-4 mt-6">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">From</label>
                  <input data-testid="block-start" type="date" disabled={!!drawer.existing} value={drawer.start} onChange={(e) => setDrawer({ ...drawer, start: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40 disabled:bg-slate-50 disabled:text-slate-500" /></div>
                <div><label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">To (checkout)</label>
                  <input data-testid="block-end" type="date" disabled={!!drawer.existing} value={drawer.end} onChange={(e) => setDrawer({ ...drawer, end: e.target.value })} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40 disabled:bg-slate-50 disabled:text-slate-500" /></div>
              </div>
              <div><label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Reason</label>
                <input data-testid="block-reason" disabled={!!drawer.existing} value={drawer.reason} onChange={(e) => setDrawer({ ...drawer, reason: e.target.value })} placeholder="Owner stay / maintenance / renovation…" className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40 disabled:bg-slate-50 disabled:text-slate-500" /></div>
              {drawer.existing ? (
                <button data-testid="block-delete" onClick={deleteBlock} disabled={busy}
                  className="w-full rounded-full py-3 text-sm font-semibold flex items-center justify-center gap-2 bg-slate-900 text-white hover:bg-red-600 transition-colors">
                  {busy ? <Loader2 className="animate-spin" size={15} /> : <Trash2 size={14} />} Remove block
                </button>
              ) : (
                <button data-testid="block-save" onClick={saveBlock} disabled={busy || !drawer.start || !drawer.end || drawer.end <= drawer.start}
                  className="btn-brand w-full rounded-full py-3 text-sm font-semibold flex items-center justify-center gap-2">
                  {busy ? <Loader2 className="animate-spin" size={15} /> : <Ban size={14} />} Block these dates
                </button>
              )}
            </div>
          </div>
        </div>
      </Portal>
      )}

      {openBooking && (
        <BookingDetailDialog
          bookingId={openBooking}
          onClose={() => setOpenBooking(null)}
          onChanged={() => setTick((t) => t + 1)}
        />
      )}
    </div>
  );
}

function UnitRows({ unit, isFirstUnit, days, rates, editRate, setEditRate, commitRate, resetRate, bookingByUnitStart, bookingSpanCover, turnoverByUnitDay, blockByUnitStart, blockSpanCover, autoBookedDays, autoBlockedDays, drag, startDrag, extendDrag, openExistingBlock, openBooking, isThisMonth, todayDay }) {
  // Render two rows: rate/inventory + booking bars (with colSpan)
  const bookingCells = [];
  const rateCells = [];
  let dd = 1;
  while (dd <= days.length) {
    const rk = `${unit.id}-${dd}`;
    const isToday = isThisMonth && dd === todayDay;
    const startedBooking = bookingByUnitStart[rk];
    const startedBlock = blockByUnitStart[rk];
    const autoBooked = autoBookedDays[rk];
    const autoBlocked = autoBlockedDays[rk];
    const coveredByBooking = bookingSpanCover[rk];
    const coveredByBlock = blockSpanCover[rk];
    const inDrag = drag && drag.unit_id === unit.id && dd >= Math.min(drag.anchor, drag.current) && dd <= Math.max(drag.anchor, drag.current);

    // Rate row: always one cell per day, showing 1 or 0 + rate (editable via double-click)
    const isOccupied = coveredByBooking || coveredByBlock || !!autoBooked || !!autoBlocked;
    const rateCell = rates[dd - 1];
    const isEditing = isFirstUnit && editRate && editRate.date === rateCell.date;
    rateCells.push(
      <td key={`r-${dd}`}
        onDoubleClick={() => isFirstUnit && setEditRate({ date: rateCell.date, value: String(rateCell.rate) })}
        title={isFirstUnit ? "Double-click to override this night's rate across every channel" : `Rate ₹${rateCell.rate}${rateCell.overridden ? " (custom)" : ""}`}
        className={`text-center py-1.5 border-b border-r border-slate-100 ${rateCell.weekend ? "bg-amber-50/30" : ""} ${isToday ? "bg-[#EEF2FF]/40" : ""} ${isFirstUnit ? "cursor-pointer hover:bg-[#EEF2FF]/60" : ""}`}>
        <div className={`text-[11px] font-bold ${isOccupied ? "text-slate-300" : "text-slate-700"}`}>{isOccupied ? 0 : 1}</div>
        {isEditing ? (
          <input data-testid={`inline-rate-${rateCell.date}`} autoFocus type="text" inputMode="decimal"
            value={editRate.value}
            onChange={(e) => setEditRate({ ...editRate, value: e.target.value.replace(/[^\d.]/g, "") })}
            onKeyDown={(e) => { if (e.key === "Enter") commitRate(); else if (e.key === "Escape") setEditRate(null); }}
            onBlur={commitRate}
            onClick={(e) => e.stopPropagation()}
            className="w-20 mx-auto block border border-[#4F46E5] rounded px-1 py-0.5 text-[11px] font-mono text-center focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40" />
        ) : (
          <div className={`text-[10px] font-mono flex items-center justify-center gap-1 ${rateCell.overridden ? "text-[#4F46E5] font-bold" : "text-slate-400"}`}>
            <span>{Number(rateCell.rate).toLocaleString("en-IN")}</span>
            {isFirstUnit && rateCell.overridden && (
              <button data-testid={`inline-rate-reset-${rateCell.date}`} onClick={(e) => { e.stopPropagation(); resetRate(rateCell.date); }} title="Reset to city default" className="text-slate-300 hover:text-slate-600 leading-none">×</button>
            )}
          </div>
        )}
      </td>
    );

    // Booking row: emit colSpan if a booking or block starts here, otherwise 1-day cell
    const turnover = turnoverByUnitDay?.[rk];
    if (turnover) {
      const outCfg = CH[turnover.outgoing.channel] || CH.Direct;
      const inCfg = CH[turnover.incoming.channel] || CH.Direct;
      bookingCells.push(
        <td key={`t-${dd}`} colSpan={1} className={`p-0 border-b border-r border-slate-100 relative ${isToday ? "bg-[#EEF2FF]/40" : ""}`} style={{ height: 40 }}>
          <div title={`↓ Checkout · ${turnover.outgoing.guest} · click for details`}
            data-testid={`cal-turnover-out-${turnover.outgoing.id}`}
            onClick={(e) => { e.stopPropagation(); openBooking?.(turnover.outgoing.id); }}
            style={{ position: "absolute", top: 6, left: 3, right: 3, bottom: 6, backgroundColor: outCfg.bg, clipPath: "polygon(0 0, 100% 0, 0 100%)", cursor: "pointer", borderRadius: 6 }} />
          <div title={`↑ Check-in · ${turnover.incoming.guest} · click for details`}
            data-testid={`cal-turnover-in-${turnover.incoming.id}`}
            onClick={(e) => { e.stopPropagation(); openBooking?.(turnover.incoming.id); }}
            style={{ position: "absolute", top: 6, left: 3, right: 3, bottom: 6, backgroundColor: inCfg.bg, clipPath: "polygon(100% 0, 100% 100%, 0 100%)", cursor: "pointer", borderRadius: 6 }} />
        </td>
      );
      dd += 1;
      continue;
    }
    if (startedBooking) {
      const { b, span } = startedBooking;
      const cfg = CH[b.channel] || CH.Direct;
      const statusOpacity = b.status === "upcoming" ? 0.88 : b.status === "cancelled" || b.status === "no_show" ? 0.35 : 1;
      bookingCells.push(
        <td key={`b-${dd}`} colSpan={span} className={`p-0 border-b border-r border-slate-100 ${isToday ? "bg-[#EEF2FF]/40" : ""}`} style={{ height: 40 }}>
          <div data-testid={`cal-booking-${b.id}`} onClick={(e) => { e.stopPropagation(); openBooking?.(b.id); }}
            title={`${b.guest} · ${b.channel} · ${b.checkin} → ${b.checkout} · ₹${Number(b.amount).toLocaleString("en-IN")}\nClick for full booking dialog`}
            className="mx-[3px] my-1.5 h-8 rounded-lg flex items-center gap-2 px-2 text-white overflow-hidden cursor-pointer select-none hover:shadow-md transition-shadow"
            style={{ backgroundColor: cfg.bg, opacity: statusOpacity, boxShadow: "0 1px 2px rgba(0,0,0,.15)" }}>
            <ChannelBadge channel={b.channel} />
            <span className="text-[12px] font-semibold truncate flex-1">{b.guest}</span>
          </div>
        </td>
      );
      dd += span;
      continue;
    }
    if (startedBlock) {
      const { bl, span } = startedBlock;
      bookingCells.push(
        <td key={`bl-${dd}`} colSpan={span} className={`p-0 border-b border-r border-slate-100 ${isToday ? "bg-[#EEF2FF]/40" : ""}`} style={{ height: 40 }}>
          <div data-testid={`cal-block-${unit.id}-${dd}`} onClick={() => openExistingBlock(bl, unit)}
            title={`${bl.reason} — ${bl.start} → ${bl.end}. No channel can sell these dates.`}
            className="mx-[3px] my-1.5 h-8 rounded-lg flex items-center justify-center gap-2 px-2 text-white cursor-pointer"
            style={{ backgroundColor: "#1E293B", backgroundImage: "repeating-linear-gradient(45deg,#1E293B,#1E293B 3px,#334155 3px,#334155 5px)", boxShadow: "0 1px 2px rgba(0,0,0,.15)" }}>
            <Ban size={11} className="opacity-70 shrink-0" />
            <span className="text-[11px] font-extrabold tracking-wider uppercase truncate">{(bl.reason || "Block").toUpperCase().includes("BLOCK") ? bl.reason : `${bl.reason} · BLOCKED`}</span>
          </div>
        </td>
      );
      dd += span;
      continue;
    }
    if (coveredByBooking || coveredByBlock) { dd++; continue; }
    // empty or auto-blocked cell
    bookingCells.push(
      <td key={`e-${dd}`} className={`p-0 border-b border-r border-slate-100 ${rates[dd - 1].weekend ? "bg-amber-50/30" : ""} ${isToday ? "bg-[#EEF2FF]/40" : ""}`}
        style={{ height: 40 }}
        onMouseEnter={() => drag && !autoBooked && !autoBlocked && extendDrag(unit, dd)}>
        {autoBooked ? (
          <div title={`Auto-blocked — ${autoBooked.guest}'s booking on a connected unit`} className="mx-[3px] my-1.5 h-8 rounded-lg"
            style={{ backgroundImage: "repeating-linear-gradient(45deg,#e2e8f0,#e2e8f0 3px,#cbd5e1 3px,#cbd5e1 5px)" }} />
        ) : autoBlocked ? (
          <div title={`Auto-blocked by manual block on unit ${autoBlocked.unit_name || ""}`} className="mx-[3px] my-1.5 h-8 rounded-lg"
            style={{ backgroundImage: "repeating-linear-gradient(45deg,#334155,#334155 3px,#475569 3px,#475569 5px)", opacity: 0.55 }} />
        ) : inDrag ? (
          <div className="mx-[3px] my-1.5 h-8 rounded-lg pointer-events-none" style={{ backgroundColor: "#4F46E5", opacity: 0.35, boxShadow: "inset 0 0 0 1px #4F46E5" }} />
        ) : (
          <button data-testid={`cal-cell-${unit.id}-${dd}`}
            onMouseDown={(e) => startDrag(unit, dd, e)}
            className="w-full h-full opacity-0 hover:opacity-100 flex items-center justify-center transition-opacity"
            title="Click or drag to block these dates">
            <span className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider">Block</span>
          </button>
        )}
      </td>
    );
    dd++;
  }

  return (
    <>
      {/* Rate/Inventory row */}
      <tr>
        <td rowSpan={2} className="sticky left-0 bg-white px-3 py-2 border-b border-r border-slate-100 align-middle z-10">
          <div className="flex items-center gap-2">
            <Home size={13} className="text-slate-400 shrink-0" />
            <span className="font-display font-bold text-slate-900 text-sm truncate">{unit.name}</span>
            <span className="ml-auto flex items-center gap-1 text-slate-300">
              <Link2 size={11} />
              <DollarSign size={11} />
            </span>
          </div>
        </td>
        {rateCells}
      </tr>
      {/* Booking bars row */}
      <tr>
        {bookingCells}
      </tr>
    </>
  );
}
